import React, { useState } from 'react';
import axios from 'axios';
import './App.css';

// API base URL - use environment variable or default to relative path for Vercel
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

function App() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [mediaItems, setMediaItems] = useState([]);
  const [downloadingItems, setDownloadingItems] = useState(new Set());
  const [isProfileUrl, setIsProfileUrl] = useState(false);

  const checkUrlType = (url) => {
    const profilePattern = /threads\.(com|net)\/@[\w.-]+\/?$/;
    const postMediaPattern = /threads\.(com|net)\/@[\w.-]+\/post\/[\w-]+\/media\/?$/;
    
    if (profilePattern.test(url.trim())) {
      return 'profile';
    } else if (postMediaPattern.test(url.trim())) {
      return 'postMedia';
    }
    return 'singlePost';
  };

  const handleUrlChange = (e) => {
    const newUrl = e.target.value;
    setUrl(newUrl);
    const urlType = checkUrlType(newUrl);
    setIsProfileUrl(urlType === 'profile' || urlType === 'postMedia');
  };

  const getUrlType = () => {
    return checkUrlType(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMediaItems([]);
    
    const urlType = checkUrlType(url);
    setLoadingMessage(
      urlType === 'profile' 
        ? '正在掃描個人檔案並提取媒體...' 
        : '正在從貼文提取所有媒體...'
    );

    try {
      const response = await axios.post(`${API_BASE_URL}/api/download`, { url });
      
      if (response.data.success) {
        // Always show media grid if we have media items
        if (response.data.mediaItems && response.data.mediaItems.length > 0) {
          setMediaItems(response.data.mediaItems);
          setLoadingMessage('');
        } else {
          setError('找不到媒體內容');
          setLoadingMessage('');
        }
      }
    } catch (err) {
      // Ensure error is always a string
      let errorMessage = '處理請求失敗';
      if (err.response?.data?.error) {
        errorMessage = typeof err.response.data.error === 'string' 
          ? err.response.data.error 
          : err.response.data.error.message || JSON.stringify(err.response.data.error);
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      setLoadingMessage('');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadMedia = async (mediaItem) => {
    const itemId = mediaItem.url;
    setDownloadingItems(prev => new Set(prev).add(itemId));

    try {
      const response = await axios.post(`${API_BASE_URL}/api/download-media`, {
        mediaUrl: mediaItem.url,
        type: mediaItem.type
      });

      if (response.data.success) {
        // If directDownload is true, use the original URL directly
        if (response.data.directDownload) {
          // Fetch the media and trigger download
          const downloadResponse = await fetch(response.data.mediaUrl);
          if (!downloadResponse.ok) {
            throw new Error('無法取得媒體檔案');
          }
          const blob = await downloadResponse.blob();
          const url = window.URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = url;
          link.download = response.data.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          // Legacy: download from server endpoint
          const downloadResponse = await fetch(response.data.mediaUrl);
          const blob = await downloadResponse.blob();
          const url = window.URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = url;
          link.download = response.data.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (err) {
      console.error('Download error:', err);
      alert('下載媒體失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setDownloadingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  return (
    <div className="App">
      <header className="header">
        <div className="container">
          <h1>免費 Threads 影片圖片下載器</h1>
          <p className="tagline">免費下載 Threads 貼文和個人檔案的影片與圖片 - 完全免費、無廣告、快速簡單</p>
        </div>
      </header>

      <main className="main-content">
        <div className="container">
          <div className="download-card">
            <form onSubmit={handleSubmit} className="form">
              <label htmlFor="url-input" className="input-label">
                {(() => {
                  const urlType = getUrlType();
                  if (urlType === 'profile') {
                    return '📚 個人檔案網址（將掃描所有媒體）';
                  } else if (urlType === 'postMedia') {
                    return '📦 貼文媒體網址（將提取此貼文的所有媒體）';
                  }
                  return '📥 貼文網址（將下載單一影片/圖片）';
                })()}
              </label>
              <div className="input-group">
                <input
                  id="url-input"
                  type="text"
                  value={url}
                  onChange={handleUrlChange}
                  placeholder={(() => {
                    const urlType = getUrlType();
                    if (urlType === 'profile') {
                      return "https://www.threads.com/@username";
                    } else if (urlType === 'postMedia') {
                      return "https://www.threads.com/@username/post/.../media";
                    }
                    return "https://www.threads.com/@username/post/...";
                  })()}
                  className="url-input"
                  disabled={loading}
                />
                <button 
                  type="submit" 
                  className="download-btn"
                  disabled={loading || !url.trim()}
                >
                  {loading ? (
                    <span className="btn-content">
                      <span className="spinner"></span>
                      {loadingMessage || '處理中...'}
                    </span>
                  ) : (
                    (() => {
                      const urlType = getUrlType();
                      if (urlType === 'profile') return '掃描個人檔案';
                      if (urlType === 'postMedia') return '提取所有媒體';
                      return '提取媒體';
                    })()
                  )}
                </button>
              </div>
              {isProfileUrl && (
                <p className="url-hint">
                  💡 {getUrlType() === 'profile' 
                    ? '個人檔案掃描將提取個人檔案中的所有影片和圖片。這可能需要 10-30 秒。'
                    : '貼文媒體提取將找出此貼文中的所有影片和圖片。這可能需要幾秒鐘。'}
                </p>
              )}
            </form>
          </div>

          <div className="instructions-card">
            <h2>使用說明</h2>
            <div className="instructions-grid">
              <div className="instruction-item">
                <div className="instruction-icon">📥</div>
                <h3>下載單一貼文</h3>
                <p>貼上 Threads 貼文網址以下載影片或圖片</p>
                <code>https://www.threads.com/@username/post/...</code>
              </div>
              <div className="instruction-item">
                <div className="instruction-icon">📚</div>
                <h3>瀏覽個人檔案媒體</h3>
                <p>貼上個人檔案網址以查看和下載所有媒體</p>
                <code>https://www.threads.com/@username</code>
              </div>
            </div>
            <div style={{marginTop: '20px', padding: '15px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd'}}>
              <p style={{margin: 0, color: '#0369a1', fontSize: '0.95rem'}}>
                <strong>✨ 完全免費</strong> • <strong>🚫 無廣告</strong> • <strong>⚡ 快速下載</strong> • <strong>🔒 安全可靠</strong>
              </p>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <strong>⚠️ 錯誤：</strong> {error}
            </div>
          )}

          {loading && loadingMessage && (
            <div className="loading-message">
              <div className="spinner-large"></div>
              <p>{loadingMessage}</p>
              <p className="loading-hint">請稍候，這可能需要一些時間...</p>
            </div>
          )}

          {mediaItems.length > 0 && (
            <div className="media-grid-container">
              <div className="media-header">
                <h2>📚 找到 {mediaItems.length} 個媒體項目</h2>
                <p className="media-subtitle">點擊「儲存」按鈕即可將任何項目下載到您的裝置</p>
              </div>
              <div className="media-grid">
                {mediaItems.map((item, index) => {
                  // Use proxy URL to bypass CORS for display
                      const proxyUrl = `${API_BASE_URL}/api/proxy-media?url=${encodeURIComponent(item.url)}`;
                  return (
                    <div key={index} className="media-item">
                      <div className="media-preview-wrapper">
                        {item.type === 'video' ? (
                          <video src={proxyUrl} controls className="media-preview" />
                        ) : (
                          <img 
                            src={proxyUrl} 
                            alt={`Media ${index + 1}`} 
                            className="media-preview"
                            loading="lazy"
                            onError={(e) => {
                              // Fallback to original URL if proxy fails
                              e.target.src = item.url;
                            }}
                          />
                        )}
                        <div className="media-type-badge">{item.type === 'video' ? '🎥 影片' : '🖼️ 圖片'}</div>
                      </div>
                      <button
                        onClick={() => handleDownloadMedia(item)}
                        className="download-media-btn"
                        disabled={downloadingItems.has(item.url)}
                      >
                        {downloadingItems.has(item.url) ? (
                          <span className="btn-content">
                            <span className="spinner-small"></span>
                            儲存中...
                          </span>
                        ) : (
                          `💾 儲存${item.type === 'video' ? '影片' : '圖片'}`
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </main>

      <footer className="footer">
        <div className="container">
          <p>免費 Threads 影片圖片下載器 | 完全免費、無廣告、快速下載 Threads 貼文和個人檔案的影片與圖片</p>
          <p style={{marginTop: '10px', fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)'}}>
            🚫 無廣告 • 💯 完全免費 • ⚡ 快速下載 • 🔒 安全可靠 • 🇹🇼 台灣最佳 Threads 下載工具
          </p>
          <p className="footer-links">
            <a href="#privacy">隱私權政策</a> • <a href="#terms">使用條款</a> • <a href="#about">關於我們</a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
